import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { uuid } from 'vue-uuid';
import { useStore } from 'vuex';

import { useLog } from './useLog.js';
import Company from "@/domain/model/company/Company.js";
import EventsDomain from '@/domain/model/events/EventsDomain.js';
import UserEvents from '@/domain/model/user/UserEvents.js';
import RcDomain from '@/domain/RcDomain.js';
import { t } from "@/i18n/index.js";
import BrowserStorage from '@/storage/BrowserStorage';
import ConstUtils from '@/utils/ConstUtils.js';
import ContentUtils from "@/utils/ContentUtils.js";
import EventUtils from '@/utils/EventUtils.js';
import StringUtils from '@/utils/StringUtils.js';

export function useConnection() {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();

  const isLoading = ref(false);
  const loadingMessage = ref('');
  const showSessionTimeoutDialog = ref(false);
  const canStart = ref(false);

  const { log } = useLog();

  const connectionDomain = computed(() => store.getters.domain);
  const auth_client = computed(() => store.getters.auth_client);
  const auth_connected = computed(() => store.getters.auth_connected);
  const auth_socket_status = computed(() => store.getters.auth_socket_status);
  const lastEvent = computed(() => store.getters.auth_socket_message);
  const currentCompany = computed(() => store.getters.AuthStorage_Company);

  const start = () => {
    canStart.value = true;
  }

  const connectSocket = () => {
    store.dispatch('connectSocket')
  }

  const callback = (message) => {
    loadingMessage.value = message;
  }

  const ensureConnection = () => {
    isLoading.value = true;
    loadingMessage.value = t('domain_status_loading');
    if (!isConnected()) {
      showSignIn();
    }
  }

  const isConnected = () => {
    if (connectionDomain.value && auth_connected.value) {
      // let session = connectionDomain.value.session();
      // let company = session.company();
      // let operatorId = ContentUtils.baseDecode(route.params.operatorId || '');
      // if (operatorId != null && company.id() != operatorId) {
      //   let newCompany = connectionDomain.value.companys().findById(operatorId);
      //   if (newCompany.isNotNew()) {
      //     session.withCompany(newCompany);
      //     session.companyName = newCompany.name();
      //     store.dispatch('AuthStorage_updateCompany', newCompany);
      //   }
      // }
      log.info("Already connected: auth_connected starting page");
      return true;
    } else {
      var value = window.localStorage.readyChek;
      if (!value) {
        return false;
      }
      var cookieData = JSON.parse(value);
      if (!cookieData) {
        return false
      }
      callback(t('domain_status_reconnecting'));
      log.info("Attempting to create connection");
      connectSocket();
    }
    return true;
  }

  const showSignIn = () => {
    router.replace({ name: route.query.redirectTo || ConstUtils.ROUTES.SIGNIN });
  }

  const createDomainSession = () => {
    createDomain();
    createSession();
    start();
  }

  const createAdminDomainSession = () => {
    const domain = createDomain();
    createSession();
    if (StringUtils.cic(route.path, "admin/accounts")) {
      let encodedCompanyId = route.params.operatorId;
      let operatorId = ContentUtils.baseDecode(encodedCompanyId);
      callback(StringUtils.format("Loading Company {0}", [operatorId]));
      loadCompany(domain);
    } else {
      start();
    }
  }

  const displayStatus = () => {
    if (!auth_connected.value) {
      showSessionTimeoutDialog.value = true;
    } else {
      status(auth_socket_status.value);
    }
  }

  const sendSignInEvent = () => {
    if (!auth_connected.value) {
      showSessionTimeoutDialog.value = true;
    } else {
      createConnection();
    }
  }

  const withAdminCompany = () => {
    var company = new Company(connectionDomain.value);
    company.withName("Administration Portal");
    connectionDomain.value.session().with(company);
    store.dispatch('AuthStorage_updateCompany', company);
  }

  const createConnection = () => {
    callback(t('domain_status_reconnecting'));
    var value = window.localStorage.readyChek;
    var cookieData = {};
    if (value) {
      cookieData = JSON.parse(value);
    }
    log.info("Sending signin event");
    let password = ContentUtils.decodeText(cookieData.password)
    const requestEvent = UserEvents.SignIn.Request(cookieData.email, password);
    requestEvent[ConstUtils.GRECAPTCHA.NAME] = '';
    requestEvent['maker'] = window.navigator.userAgent;
    EventsDomain.initEvent(
      requestEvent, null,
      uuid.v1(),
      auth_client.value.deviceId,
      auth_client.value.version);
    EventUtils.Send.event(auth_client.value.socket.connection, requestEvent);
  }

  const createDomain = () => {
    var storage = new BrowserStorage(store.getters);
    callback(t('domain_status_creating_session'));
    log.info("Creating domain");
    const mockPanel = {
      auth_client: auth_client.value,
      addListenerAction: (listener) => store.dispatch('addListenerAction', listener),
      removeListenerAction: (listener) => store.dispatch('removeListenerAction', listener),
      updateDomain: (newDomain) => store.dispatch('updateDomain', newDomain),
      $store: store,
      $connect: () => store.dispatch('connectSocket'),
    };
    var domain = new RcDomain(mockPanel, storage);
    domain.withStorage(store.getters).withLogger(window.console).start();
    domain.events().withListeners(mockPanel.addListenerAction, mockPanel.removeListenerAction);
    store.dispatch('updateDomain', domain);
    callback(t('shell_status_done'));
    return domain;
  }

  const createSession = () => {
    var session = connectionDomain.value.session();
    var value = window.localStorage.readyChek;
    var cookieData = {};

    if (value) {
      cookieData = JSON.parse(value);
    }
    var companyId = cookieData['Company'];
    var userId = cookieData['User'];
    var user = session.user();
    if (user.isNew()) {
      user = connectionDomain.value.users().findById(userId);
    }
    var employees = connectionDomain.value.employees().findByUserId(user.id());

    if (StringUtils.isEmpty(companyId)) {
      const roles = employees.roles().find();
      if (roles.isAdministrator()) {
        session.with(employees.administrator());
        withAdminCompany();
        return session;
      }
      router.replace({ name: route.query.redirectTo || ConstUtils.ROUTES.SIGNIN });
      return session;
    }
    var employee = employees.findByCompanyId(companyId);
    if (employees.length() > 1 && employee.isNew()) {
      router.replace({ name: route.query.redirectTo || ConstUtils.ROUTES.COMPANY.SELECT });
    } else if (employees.length() == 0) {
      showSignIn();
    } else {
      var company = employee.company().find();
      log.info("User: {0}", [employee.user().find().fullName()]);
      log.info("Employee for company: {0}", [company.name()]);
      session.with(company);
      session.with(employee);
      store.dispatch('AuthStorage_updateCompany', company);
      var roles = session.employee().roles().find();
      if (roles.isAdministrator()) {
        withAdminCompany();
      }
    }
    return session;
  }

  const status = (status) => {
    if (!status) {
      callback(t('domain_status_loading'));

    } else {
      if (status == 'findingYou') {
        callback(t('domain_status_finding_you'));
      } else if (status == 'findingSchedules') {
        callback(t('domain_status_finding_schedules'));
      } else if (status == 'findingInspections') {
        callback(t('domain_status_finding_inspections'));
      } else if (status == 'findingTimesheets') {
        callback(t('domain_status_finding_hours'));
      } else {
        callback(t('domain_status_storing_data'))
      }
    }
  }

  const adminInit = () => {
    isLoading.value = true;
    if (connectionDomain.value) {
      var session = connectionDomain.value.session();
      session.companyName = null;
      session.withoutCompany();
    }
  }

  const loadCompany = (givenDomain = null) => {
    var domain = givenDomain ?? connectionDomain.value;
    var company = domain.session().company();
    let companyId = ContentUtils.baseDecode(route.params.operatorId || '');
    var fetch = company.id() != companyId;
    if (company.isPointer()) {
      fetch = true;
    }

    if (!companyId || !companyId.startsWith("#")) {
      fetch = false;
    }

    if (fetch) {
      isLoading.value = true;
      domain.session().companyName = null;
      loadingMessage.value = t('domain_status_loading_company');
      var event = domain.events().company().details(companyId);
      event.send(loadedCompanyListener, {domain: domain});
    } else {
      isLoading.value = false;
    }
  }

  const loadedCompanyListener = (notification, context) => {
    context["completed"] = new Date();
    let domain = context["domain"];
    var payload = notification.payload();
    var data = payload.valuesFor(Company.MODEL_NAME);
    var company = new Company(domain, data);

    domain.session().withCompany(company);
    domain.session().companyName = company.name();
    store.dispatch('AuthStorage_updateCompany', company);
    setTimeout(() => {
      start();
    }, 250);
  };
  
  const adminLoadCompany = (company, pageName) => {
    if (company.isPointer()) {
      adminLoadCompanyRedirect(pageName);
    } else {
      let session = connectionDomain.value.session();
      session.with(company);
      session.companyName = company.name();
      store.dispatch('AuthStorage_updateCompany', company);
    }
  }

  const adminLoadCompanyRedirect = (pageName) => {
    let listPath = ConstUtils.ROUTES.SHARED.LOAD.COMPANY_PATH;
    router.push({
      path: listPath,
      query: {
        companyId: route.params.operatorId,
        page: pageName,
      }
    });
  }

  const adminIsAdministrator = () => {
    var user = connectionDomain.value.session().user();
    var employees = connectionDomain.value.employees().findByUserId(user.id());
    var roles = employees.roles().find();
    return roles.isAdministrator();
  }

  const ensureCompanyLoaded = (pageName) => {
    let companyId = ContentUtils.baseDecode(route.params.operatorId || '');
    if (adminIsAdministrator() && StringUtils.isNotEmpty(companyId)) {
      let company = connectionDomain.value.companies().findById(companyId);
      if (company.isNotNew()) {
        adminLoadCompany(company, pageName);
      } else {
        adminLoadCompanyRedirect(pageName);
      }
    }
  }

  return {
    isLoading,
    loadingMessage,
    showSessionTimeoutDialog,
    canStart,
    log,
    start,
    ensureConnection,
    displayStatus,
    sendSignInEvent,
    createDomainSession,
    createAdminDomainSession,
    status,
    adminInit,
    loadCompany,
    adminIsAdministrator,
    ensureCompanyLoaded,
    lastEvent,
    currentCompany,
    withAdminCompany,
  }
}